import { safeParseInt } from "../utils/safeParseNumber";

export enum CaSections {
  FacilityInformation = "FacilityInformation",
  HazardousMaterialsInventory = "HazardousMaterialsInventory",
  EmergencyResponseAndTrainingPlan = "EmergencyResponseAndTrainingPlan",
  UndergroundStorageTanks = "UndergroundStorageTanks",
  TieredPermitting = "TieredPermitting",
  RecyclableMaterialsReport = "RecyclableMaterialsReport",
  RemoteWasteConsolidationSiteAnnualNotification = "RemoteWasteConsolidationSiteAnnualNotification",
  HazardousWasteTankClosureCertification = "HazardousWasteTankClosureCertification",
  AbovegroundPetroleumStorageAct = "AbovegroundPetroleumStorageAct",
  CaliforniaAccidentalReleaseProgram = "CaliforniaAccidentalReleaseProgram",
}

export const SectionIds = {
  [CaSections.FacilityInformation]: "1",
  [CaSections.HazardousMaterialsInventory]: "2",
  [CaSections.EmergencyResponseAndTrainingPlan]: "3",
  [CaSections.UndergroundStorageTanks]: "4",
  [CaSections.TieredPermitting]: "5",
  [CaSections.RecyclableMaterialsReport]: "6",
  [CaSections.RemoteWasteConsolidationSiteAnnualNotification]: "7",
  [CaSections.HazardousWasteTankClosureCertification]: "8",
  [CaSections.AbovegroundPetroleumStorageAct]: "9",
  [CaSections.CaliforniaAccidentalReleaseProgram]: "10",
} as const satisfies Record<CaSections, string>;

export type SectionIdValues = (typeof SectionIds)[keyof typeof SectionIds];

export const SectionLabels: Record<CaSections, string> = {
  [CaSections.FacilityInformation]: "Facility Information",
  [CaSections.HazardousMaterialsInventory]: "Hazardous Materials Inventory",
  [CaSections.EmergencyResponseAndTrainingPlan]: "Emergency Response and Training Plans",
  [CaSections.UndergroundStorageTanks]: "Underground Storage Tanks",
  [CaSections.TieredPermitting]: "Tiered Permitting",
  [CaSections.RecyclableMaterialsReport]: "Recyclable Materials Report",
  [CaSections.RemoteWasteConsolidationSiteAnnualNotification]:
    "Remote Waste Consolidation Site Annual Notification",
  [CaSections.HazardousWasteTankClosureCertification]: "Hazardous Waste Tank Closure Certification",
  [CaSections.AbovegroundPetroleumStorageAct]: "Aboveground Petroleum Storage Act",
  [CaSections.CaliforniaAccidentalReleaseProgram]: "California Accidental Release Program",
};

/** A deduped, validated set of CERS HMBP section IDs. Always includes FacilityInformation. */
export type SelectedSections = ReadonlySet<SectionIdValues>;

export const FACILITY_INFORMATION_SECTION_ID: SectionIdValues =
  SectionIds[CaSections.FacilityInformation];

export const ALL_CA_SECTION_IDS: readonly SectionIdValues[] = Object.values(SectionIds);

const VALID_SECTION_IDS: ReadonlySet<string> = new Set(Object.values(SectionIds));

// Case-insensitive lookup from CaSections enum name → numeric SectionId. Lets
// callers pass "FacilityInformation" or "facilityinformation" instead of "1".
const SECTION_NAME_TO_ID: ReadonlyMap<string, SectionIdValues> = new Map(
  (Object.values(CaSections) as CaSections[]).map((name) => [name.toLowerCase(), SectionIds[name]])
);

function isValidSectionId(value: string): value is SectionIdValues {
  return VALID_SECTION_IDS.has(value);
}

/**
 * Resolve a single token (numeric ID or CaSections enum name) to a SectionIdValues.
 * Returns undefined for unknown tokens.
 */
export function resolveSectionToken(token: string): SectionIdValues | undefined {
  if (typeof token !== "string") return undefined;
  const trimmed = token.trim();
  if (trimmed === "") return undefined;
  if (isValidSectionId(trimmed)) return trimmed;
  return SECTION_NAME_TO_ID.get(trimmed.toLowerCase());
}

/**
 * Normalize a list of section identifiers into a SelectedSections set.
 * - Accepts numeric IDs ("1") or CaSections enum names ("FacilityInformation"),
 *   case-insensitive on names.
 * - Drops unknown tokens.
 * - Dedupes.
 * - Force-includes FacilityInformation (CERS rule: Facility Info submits with every report).
 */
export function forceFacilityInformation(input: readonly string[]): SelectedSections {
  const result = new Set<SectionIdValues>();
  result.add(FACILITY_INFORMATION_SECTION_ID);
  for (const token of input) {
    const id = resolveSectionToken(token);
    if (id !== undefined) {
      result.add(id);
    }
  }
  return result;
}

/**
 * Numeric comparator for CERS HMBP section IDs. Section IDs are stringified
 * numbers ("1"-"10"), so the default lexicographic sort orders "10" before "2".
 * Use this everywhere section IDs are sorted for display, logging, or
 * persistence so the three call sites (job creation, runtime, UI) cannot drift.
 * Falls back to lexicographic compare when a token is not a parseable number
 * so an unexpected garbage input does not collapse to a meaningless NaN sort.
 */
export function compareSectionIds(a: string, b: string): number {
  const na = safeParseInt(a);
  const nb = safeParseInt(b);
  if (na === null || nb === null) {
    return a.localeCompare(b);
  }
  return na - nb;
}

/**
 * Parse an unknown value (e.g. Job.parameters.sectionIds from JSON) into a SelectedSections.
 * Returns null when the value is absent. Returns a non-empty Set otherwise (FI force-included).
 */
export function parseSectionIds(value: unknown): SelectedSections | null {
  if (value === null || value === undefined) return null;
  if (!Array.isArray(value)) return null;
  const stringValues = value.filter((v): v is string => typeof v === "string");
  return forceFacilityInformation(stringValues);
}
